import {
	availabilityHeatmapSchema,
	availabilityPatternSchema,
	type SetAvailabilityPattern,
} from "../schemas/club-availability";
import { fetchApi } from "./internal";

// Club-wide member availability. The collection route is the aggregate
// heatmap and is admin-only; a member reads and writes their own pattern
// through /me.

export type HeatmapParams = {
	from?: Date;
	to?: Date;
	// A Go duration string, e.g. "30m". The service accepts 5m to 24h.
	granularity?: string;
	// Narrows the counted population to one club_skill_levels row — the
	// club-private label, never the public cross-club tier on users. Both the
	// per-cell counts and total_members reflect the filtered population, so a
	// filtered grid stays readable as a ratio.
	skillLevelId?: string;
};

function heatmapQuery(params?: HeatmapParams): string {
	const searchParams = new URLSearchParams();
	if (params?.from) searchParams.set("from", params.from.toISOString());
	if (params?.to) searchParams.set("to", params.to.toISOString());
	if (params?.granularity) searchParams.set("granularity", params.granularity);
	if (params?.skillLevelId) searchParams.set("skill_level_id", params.skillLevelId);
	const qs = searchParams.toString();
	return qs ? `?${qs}` : "";
}

// The wire carries instants, so exception bounds go out as RFC3339 rather
// than as whatever a Date stringifies to by default.
function toWire(data: SetAvailabilityPattern) {
	return {
		exceptions: data.exceptions.map((exception) => ({
			...exception,
			ends_at: exception.ends_at.toISOString(),
			starts_at: exception.starts_at.toISOString(),
		})),
		rules: data.rules,
	};
}

export const memberAvailability = {
	forUser: (clubId: string, userId: string) =>
		fetchApi(`/clubs/${clubId}/availability/${userId}`, availabilityPatternSchema),
	heatmap: (clubId: string, params?: HeatmapParams) =>
		fetchApi(`/clubs/${clubId}/availability${heatmapQuery(params)}`, availabilityHeatmapSchema),
	me: (clubId: string) => fetchApi(`/clubs/${clubId}/availability/me`, availabilityPatternSchema),
	setForUser: (clubId: string, userId: string, data: SetAvailabilityPattern) =>
		fetchApi(`/clubs/${clubId}/availability/${userId}`, availabilityPatternSchema, {
			body: JSON.stringify(toWire(data)),
			method: "PUT",
		}),
	setMine: (clubId: string, data: SetAvailabilityPattern) =>
		fetchApi(`/clubs/${clubId}/availability/me`, availabilityPatternSchema, {
			body: JSON.stringify(toWire(data)),
			method: "PUT",
		}),
};
